import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { addDrillWords } from '../services/drillWordsService';

// ── Types ────────────────────────────────────────────────────────────────────

interface FastAttackInfo {
  plan: string;
  day: number;
  totalDays: number;
  group: number;
  wordsRemaining: number;
}

interface HomeUser {
  xp: number;
  streakDays: number;
  wordsSeen: number;
  accuracy: number;
  fastAttack: FastAttackInfo;
  recentLevelUp: boolean;
  weakWords: string[];
  displayName: string;
}

const mockUser: HomeUser = {
  xp: 620,
  streakDays: 12,
  wordsSeen: 847,
  accuracy: 74,
  fastAttack: { plan: 'Sprint', day: 12, totalDays: 30, group: 4, wordsRemaining: 28 },
  recentLevelUp: true,
  weakWords: ['perfidious', 'laconic', 'sanguine'],
  displayName: 'there',
};

// ── Level system ─────────────────────────────────────────────────────────────

const LEVELS = [
  { level: 1, name: 'Novice' },
  { level: 2, name: 'Student',        minXp: 50   },
  { level: 3, name: 'Apprentice',     minXp: 150  },
  { level: 4, name: 'Scholar',        minXp: 275  },
  { level: 5, name: 'Linguist',       minXp: 400  },
  { level: 6, name: 'Scribe',         minXp: 500  },
  { level: 7, name: 'Wordsmith',      minXp: 600  },
  { level: 8, name: 'Lexicon Master', minXp: 1000 },
  { level: 9, name: 'Grandmaster',    minXp: 1750 },
] as { level: number; name: string; minXp: number }[];

LEVELS[0].minXp = 0;

function getLevel(xp: number) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].minXp) {
      return { current: LEVELS[i], next: LEVELS[i + 1] ?? null };
    }
  }
  return { current: LEVELS[0], next: LEVELS[1] };
}

// ── Responsive hook ───────────────────────────────────────────────────────────

function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return width;
}

// ── Colors ────────────────────────────────────────────────────────────────────

const PRIMARY   = 'var(--color-primary)';
const SURFACE   = 'var(--color-surface)';
const BORDER    = 'var(--color-border)';
const MUTED     = 'var(--color-text-muted)';
const BG        = 'var(--color-background)';
const AMBER     = 'var(--color-amber)';

// ── Confetti ──────────────────────────────────────────────────────────────────

const CONFETTI_COLORS = ['#1F4C40','#478475','#D97706','#F59E0B','#34D399','#6EE7B7','#FCD34D','#A78BFA','#F472B6'];

function Confetti() {
  const pieces = Array.from({ length: 14 });
  return (
    <>
      {pieces.map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            top: 0,
            left: `${(i / 14) * 100}%`,
            width: 8,
            height: 8,
            borderRadius: i % 3 === 0 ? '50%' : 2,
            backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animation: `confettiFall ${1.5 + (i % 4) * 0.3}s ease-in ${(i % 6) * 0.15}s forwards`,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
}

// ── SVG icons ─────────────────────────────────────────────────────────────────

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const HomeIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M3 12L12 3l9 9" /><path d="M9 21V12h6v9" /><path d="M3 12v9h18V12" />
  </svg>
);

const FlashcardIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M6 6h12v12H6z" /><path d="M10 10h6M10 14h4" />
  </svg>
);

const FastAttackIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M13 2L6 14h5l-1 8 7-12h-5z" />
  </svg>
);

const HardTermsIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 2s5 5 5 9a5 5 0 0 1-10 0c0-4 5-9 5-9z" /><path d="M12 22v-4" />
  </svg>
);

const QuantIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M4 20h16" /><path d="M10 20V4h4v16" /><path d="M4 10h16" />
  </svg>
);

const SynonymIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="8" cy="12" r="3" /><circle cx="16" cy="12" r="3" /><path d="M11 12h2" />
  </svg>
);

// ── Section label ─────────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED, margin: '0 0 10px' }}>
      {children}
    </p>
  );
}

// ── LevelXPCard ───────────────────────────────────────────────────────────────

function LevelXPCard({ user }: { user: HomeUser }) {
  const { current, next } = getLevel(user.xp);
  const fillPct = next ? Math.min(100, Math.round((user.xp / next.minXp) * 100)) : 100;
  const toNext = next ? next.minXp - user.xp : 0;

  return (
    <div style={{ backgroundColor: PRIMARY, borderRadius: 20, padding: '20px 20px 18px', color: '#fff' }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', margin: '0 0 6px' }}>
        Your Progress
      </p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 24, fontWeight: 800, fontFamily: 'Georgia, serif', color: '#fff' }}>
          {current.name}
        </span>
        <span style={{
          backgroundColor: AMBER, color: '#fff',
          fontSize: 11, fontWeight: 700, padding: '3px 10px',
          borderRadius: 999, letterSpacing: '0.04em',
        }}>
          Level {current.level}
        </span>
      </div>
      <div style={{ height: 7, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.2)', overflow: 'hidden', marginBottom: 8 }}>
        <div style={{
          height: '100%', borderRadius: 999,
          backgroundColor: '#34D399',
          width: `${fillPct}%`,
          transition: 'width 700ms ease',
        }} />
      </div>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', margin: 0 }}>
        {user.xp} xp{next ? ` · ${toNext} to ${next.name}` : ' · Max level'}
      </p>
    </div>
  );
}

// ── StatRow ───────────────────────────────────────────────────────────────────

function StatRow({ user, vertical = false }: { user: HomeUser; vertical?: boolean }) {
  const stats = [
    { value: user.streakDays, label: 'day streak' },
    { value: user.wordsSeen.toLocaleString(), label: 'words seen' },
    { value: `${user.accuracy}%`, label: 'accuracy' },
  ];

  return (
    <div style={{
      display: 'flex',
      flexDirection: vertical ? 'column' : 'row',
      gap: 8,
    }}>
      {stats.map(s => (
        <div key={s.label} style={{
          flex: vertical ? undefined : 1,
          backgroundColor: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 16,
          padding: '14px 12px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-text)', lineHeight: 1 }}>{s.value}</div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── ResumeCard ────────────────────────────────────────────────────────────────

function ResumeCard({ fa, onResume, hasStarted }: { fa: FastAttackInfo; onResume: () => void; hasStarted: boolean }) {
  return (
    <div style={{
      backgroundColor: PRIMARY, borderRadius: 20, padding: '18px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', flexShrink: 0,
        }}>
          <PlayIcon />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              backgroundColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.85)',
              padding: '2px 8px', borderRadius: 999,
            }}>
              Fast Attack · Day {fa.day}
            </span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'Georgia, serif', color: '#fff', lineHeight: 1.2 }}>
            {fa.plan} Plan
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
            Day {fa.day} of {fa.totalDays}
          </div>
        </div>
      </div>
      <button
        onClick={onResume}
        style={{
          backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff',
          border: '1.5px solid rgba(255,255,255,0.3)',
          borderRadius: 999, padding: '8px 18px',
          fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {hasStarted ? 'Resume' : 'Start'}
      </button>
    </div>
  );
}

// ── TodaysPlanCard ────────────────────────────────────────────────────────────

function TodaysPlanCard({ fa }: { fa: FastAttackInfo }) {
  const visibleDays = Math.min(fa.totalDays, 15);

  return (
    <div style={{ backgroundColor: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 20, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <Eyebrow>Today's Plan</Eyebrow>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>
            {fa.plan} Plan · Day {fa.day} of {fa.totalDays}
          </div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
            Groups {fa.group}, {fa.group + 1} + review Group {Math.max(1, fa.group - 2)} due today
          </div>
        </div>
        <span style={{
          backgroundColor: 'var(--color-success-light)', color: 'var(--color-confirmation)',
          fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, flexShrink: 0,
        }}>
          on track
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {Array.from({ length: visibleDays }, (_, i) => {
          const day = i + 1;
          const completed = day < fa.day;
          const active = day === fa.day;
          return (
            <div key={day} style={{
              width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700,
              backgroundColor: completed ? PRIMARY : active ? '#22c55e' : 'transparent',
              border: completed ? 'none' : active ? '2.5px solid #fff' : `1.5px solid ${BORDER}`,
              outline: active ? `2px solid #22c55e` : 'none',
              color: completed || active ? '#fff' : MUTED,
            }}>
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AllModesGrid ──────────────────────────────────────────────────────────────

interface ModeCard {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  stat: string;
  badge: string;
}

const MODE_BASES: Omit<ModeCard, 'stat' | 'badge'>[] = [
  { id: 'flashcards', label: 'Flashcards', icon: <FlashcardIcon />, path: '/flashcards' },
  { id: 'synonyms',   label: 'Synonyms',   icon: <SynonymIcon />,   path: '/synonyms'   },
  { id: 'hard-terms', label: 'Hard Terms', icon: <HardTermsIcon />, path: '/hard-terms' },
  { id: 'quant',      label: 'Quant',      icon: <QuantIcon />,     path: '/quant'      },
];

function AllModesGrid({ isTablet, onNavigate, modes }: { isTablet: boolean; onNavigate: (path: string) => void; modes: ModeCard[] }) {
  return (
    <div>
      <Eyebrow>All Modes</Eyebrow>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isTablet ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
        gap: 10,
      }}>
        {modes.map(m => (
          <button
            key={m.id}
            onClick={() => onNavigate(m.path)}
            style={{
              backgroundColor: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 16, padding: '14px 12px',
              textAlign: 'left', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}
          >
            <span style={{ color: PRIMARY }}>{m.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>{m.label}</span>
            <span style={{ fontSize: 11, color: MUTED }}>{m.stat}</span>
            <span style={{
              alignSelf: 'flex-start',
              backgroundColor: 'var(--color-success-light)', color: 'var(--color-confirmation)',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
            }}>
              {m.badge}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── WeakWordsCard ─────────────────────────────────────────────────────────────

function WeakWordsCard({ words, userId }: { words: string[]; userId?: string }) {
  const navigate = useNavigate();
  if (words.length === 0) return null;
  return (
    <div style={{ backgroundColor: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 20, padding: '18px 20px' }}>
      <Eyebrow>Weak Words</Eyebrow>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {words.map(w => (
          <span key={w} style={{
            backgroundColor: 'var(--color-wrong-light)', color: 'var(--color-danger)',
            border: '1px solid var(--color-wrong-border)',
            fontSize: 13, fontWeight: 600, padding: '5px 12px', borderRadius: 999,
          }}>
            {w}
          </span>
        ))}
      </div>
      <button
        onClick={async () => {
          if (userId) await addDrillWords(userId, words);
          navigate('/flashcards?drill=1');
        }}
        style={{
          backgroundColor: PRIMARY, color: '#fff',
          border: 'none', borderRadius: 999,
          fontSize: 13, fontWeight: 700, padding: '9px 20px', cursor: 'pointer',
        }}
      >
        Drill these →
      </button>
    </div>
  );
}

// ── DesktopNav ────────────────────────────────────────────────────────────────

const DESKTOP_NAV = [
  { label: 'Home',       icon: <HomeIcon />,       path: '/'            },
  { label: 'Flashcards', icon: <FlashcardIcon />,  path: '/flashcards'  },
  { label: 'Fast Attack',icon: <FastAttackIcon />, path: '/fast-attack' },
  { label: 'Hard Terms', icon: <HardTermsIcon />,  path: '/hard-terms'  },
  { label: 'Quant',      icon: <QuantIcon />,      path: '/quant'       },
];

function DesktopNav({ onNavigate, currentPath }: { onNavigate: (path: string) => void; currentPath: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {DESKTOP_NAV.map(item => {
        const active = item.path === '/' ? currentPath === '/' : currentPath.startsWith(item.path);
        return (
          <button
            key={item.path}
            onClick={() => onNavigate(item.path)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
              backgroundColor: active ? 'var(--color-success-light)' : 'transparent',
              color: active ? PRIMARY : MUTED,
              fontSize: 14, fontWeight: active ? 700 : 400,
              textAlign: 'left',
            }}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function HomePage() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isTablet  = width >= 768;
  const isDesktop = width >= 1024;

  const [homeData, setHomeData] = useState<HomeUser>(mockUser);
  const [modeStats, setModeStats] = useState<ModeCard[]>(MODE_BASES.map(m => ({ ...m, stat: '—', badge: '—' })));
  const [loading, setLoading] = useState(true);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [profile, setProfile] = useState<{ full_name: string | null } | null>(null);
  const [fastAttackStarted, setFastAttackStarted] = useState(false);

  // Greeting based on time of day
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    if (!authUser) { setLoading(false); return; }

    const uid = authUser.id;
    Promise.all([
      supabase.from('user_stats').select('*').eq('user_id', uid).maybeSingle(),
      supabase.rpc('get_weak_words', { p_user_id: uid, p_limit: 3 }),
      supabase.from('profiles').select('full_name').eq('user_id', uid).maybeSingle(),
      // Flashcards: one row per word (upserted on word_id), so count = words seen
      supabase.from('flashcard_answers').select('correct', { count: 'exact' }).eq('user_id', uid),
      // Synonyms
      supabase.from('synonym_progress').select('completed').eq('user_id', uid),
      // Hard Terms: one row per word
      supabase.from('hard_terms_answers').select('correct', { count: 'exact' }).eq('user_id', uid),
      // Fast Attack from Supabase
      supabase.from('fast_attack_progress').select('current_day, total_days').eq('user_id', uid).maybeSingle(),
      // Quant
      supabase.from('quant_problems').select('passed').eq('user_id', uid),
    ]).then(([
      { data: stats },
      { data: weakWordsRows },
      { data: prof },
      { data: flashRows, count: flashCount },
      { data: synRows },
      { data: htRows, count: htCount },
      { data: faData },
      { data: quantRows },
    ]) => {
      setProfile(prof);

      const weakWords = (weakWordsRows ?? []).map((r: { word: string }) => r.word);
      const displayName = prof?.full_name ?? authUser.email?.split('@')[0] ?? 'there';

      // ── Mode stats ────────────────────────────────────────────────────────
      // Flashcards (flashcard_answers covers both Flashcard and Fast Attack)
      const flashSeen = flashCount ?? 0;
      const flashCorrect = (flashRows ?? []).filter((r: { correct: boolean }) => r.correct).length;
      const flashAccuracy = flashSeen ? Math.round((flashCorrect / flashSeen) * 100) : 0;

      // Synonyms
      const synMatched = (synRows ?? []).filter((r: { completed: boolean }) => r.completed).length;
      const synIncomplete = (synRows ?? []).filter((r: { completed: boolean }) => !r.completed).length;

      // Hard Terms
      const htSeen = htCount ?? 0;
      const htCorrect = (htRows ?? []).filter((r: { correct: boolean }) => r.correct).length;
      const htAccuracy = htSeen ? Math.round((htCorrect / htSeen) * 100) : 0;

      // Accuracy: live answer tables (flashcard + hard terms); fallback to user_stats
      const totalCorrect  = flashCorrect + htCorrect;
      const totalAnswered = flashSeen + htSeen;
      const accuracy = totalAnswered > 0
        ? Math.round((totalCorrect / totalAnswered) * 100)
        : (stats ? Math.round((stats.total_correct / Math.max(stats.total_answered, 1)) * 100) : 0);

      // Quant
      const quantTotal = (quantRows ?? []).length;
      const quantPassed = (quantRows ?? []).filter((r: { passed: boolean }) => r.passed).length;
      const quantPass = quantTotal ? Math.round((quantPassed / quantTotal) * 100) : 0;

      setModeStats([
        { ...MODE_BASES[0], stat: flashSeen ? `${flashSeen} seen` : 'No sessions yet',       badge: `${flashAccuracy}% avg` },
        { ...MODE_BASES[1], stat: synMatched ? `${synMatched} matched` : 'No sessions yet',  badge: (synRows ?? []).length === 0 ? 'Not started' : synIncomplete ? 'In progress' : 'All done' },
        { ...MODE_BASES[2], stat: htSeen ? `${htSeen} seen` : 'No sessions yet',             badge: `${htAccuracy}% avg` },
        { ...MODE_BASES[3], stat: quantTotal ? `${quantTotal} logged` : 'No sessions yet',   badge: quantTotal ? `${quantPass}% pass` : '—' },
      ]);

      // Fast Attack — default to Day 1 if no Supabase row yet
      setFastAttackStarted(!!faData);
      const fastAttack: FastAttackInfo = faData
        ? { plan: 'Sprint', day: faData.current_day ?? 1, totalDays: faData.total_days ?? 30, group: 1, wordsRemaining: 0 }
        : { plan: 'Sprint', day: 1, totalDays: 30, group: 1, wordsRemaining: 0 };

      const newData: HomeUser = {
        xp:          stats?.xp          ?? 0,
        streakDays:  stats?.streak_days  ?? 0,
        wordsSeen:   flashSeen + htSeen, // flashcard_answers covers Flashcard + Fast Attack
        accuracy,
        fastAttack,
        recentLevelUp: false,
        weakWords,
        displayName,
      };

      setHomeData(newData);
      setLoading(false);

      // Show level-up overlay once per session if XP crossed a threshold recently
      const levelUpKey = `phrenos_levelup_shown_${newData.xp}`;
      if (!sessionStorage.getItem(levelUpKey) && newData.xp > 0) {
        // Check if level changed (compare stored xp level to current)
        const prevXp = Number(sessionStorage.getItem('phrenos_prev_xp') ?? 0);
        const prev = getLevel(prevXp);
        const curr = getLevel(newData.xp);
        if (curr.current.level > prev.current.level) {
          setShowLevelUp(true);
          sessionStorage.setItem(levelUpKey, '1');
        }
        sessionStorage.setItem('phrenos_prev_xp', String(newData.xp));
      }
    });
  }, [authUser]);

  const handleSaveName = async () => {
    if (!authUser || !nameInput.trim()) return;
    setSavingName(true);
    await supabase.from('profiles').upsert({ user_id: authUser.id, full_name: nameInput.trim() });
    setProfile({ full_name: nameInput.trim() });
    setHomeData(d => ({ ...d, displayName: nameInput.trim() }));
    setSavingName(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: MUTED, textAlign: 'center', fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  const { current: currentLevel } = getLevel(homeData.xp);

  // ── Level-up overlay ──────────────────────────────────────────────────────
  const LevelUpOverlay = showLevelUp ? (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      <Confetti />
      <div style={{
        backgroundColor: SURFACE, borderRadius: 24,
        padding: '36px 32px', textAlign: 'center', maxWidth: 320, width: '90%',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
        <div style={{
          fontSize: 28, fontWeight: 800, fontFamily: 'Georgia, serif',
          color: PRIMARY, marginBottom: 6,
          animation: 'levelUpBounce 0.5s ease forwards',
        }}>
          Level Up!
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
          {currentLevel.name}
        </div>
        <div style={{ fontSize: 13, color: MUTED, marginBottom: 24 }}>
          You reached Level {currentLevel.level}. Keep going!
        </div>
        <button
          onClick={() => setShowLevelUp(false)}
          style={{
            backgroundColor: PRIMARY, color: '#fff',
            border: 'none', borderRadius: 999,
            fontSize: 14, fontWeight: 700, padding: '11px 28px', cursor: 'pointer',
          }}
        >
          Continue →
        </button>
      </div>
    </div>
  ) : null;

  // ── Level-up amber banner ─────────────────────────────────────────────────
  const LevelUpBanner = homeData.recentLevelUp && !bannerDismissed ? (
    <div style={{
      backgroundColor: AMBER, color: '#fff',
      padding: '10px 16px', fontSize: 13, fontWeight: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    }}>
      <span>You reached {currentLevel.name} — keep going!</span>
      <button
        onClick={() => setBannerDismissed(true)}
        style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0 }}
      >
        ×
      </button>
    </div>
  ) : null;

  // ── Name prompt for magic-link users ─────────────────────────────────────
  const NamePrompt = profile?.full_name == null ? (
    <div style={{
      backgroundColor: SURFACE, border: `1px solid ${BORDER}`,
      borderRadius: 16, padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 13, color: MUTED, flexShrink: 0 }}>What should we call you?</span>
      <input
        value={nameInput}
        onChange={e => setNameInput(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleSaveName()}
        placeholder="Your name"
        style={{
          flex: 1, minWidth: 120, border: `1px solid ${BORDER}`, borderRadius: 8,
          padding: '6px 10px', fontSize: 13, backgroundColor: BG,
          outline: 'none',
        }}
      />
      <button
        onClick={handleSaveName}
        disabled={savingName || !nameInput.trim()}
        style={{
          backgroundColor: PRIMARY, color: '#fff', border: 'none',
          borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
          cursor: savingName ? 'not-allowed' : 'pointer', flexShrink: 0,
        }}
      >
        {savingName ? 'Saving…' : 'Save'}
      </button>
    </div>
  ) : null;

  // ── Responsive wrapper ────────────────────────────────────────────────────
  const contentPadding = isTablet ? '0 24px' : '0 16px';
  const maxWidth = isDesktop ? 1100 : isTablet ? 720 : '100%';

  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <LevelXPCard user={homeData} />
      <StatRow user={homeData} vertical={isDesktop} />
      {isDesktop && (
        <div style={{ marginTop: 8 }}>
          <DesktopNav onNavigate={navigate} currentPath={window.location.pathname} />
        </div>
      )}
    </div>
  );

  const mainContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <p style={{ fontSize: 13, color: MUTED, margin: '0 0 2px', textAlign: 'center' }}>{greeting},</p>
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: 'var(--color-text)', textAlign: 'center' }}>
          {homeData.displayName}
        </h1>
      </div>
      {NamePrompt}
      {!isDesktop && <LevelXPCard user={homeData} />}
      {!isDesktop && <StatRow user={homeData} />}
      <ResumeCard fa={homeData.fastAttack} onResume={() => navigate('/fast-attack')} hasStarted={fastAttackStarted} />
      <TodaysPlanCard fa={homeData.fastAttack} />
      <AllModesGrid isTablet={isTablet} onNavigate={navigate} modes={modeStats} />
      <WeakWordsCard words={homeData.weakWords} userId={authUser?.id} />
    </div>
  );

  return (
    <>
      {LevelUpOverlay}
      {LevelUpBanner}

      <div style={{ padding: contentPadding, maxWidth, margin: '0 auto' }}>
        {isDesktop ? (
          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 28, paddingTop: 24 }}>
            <div style={{ position: 'sticky', top: 64, alignSelf: 'start' }}>
              {sidebarContent}
            </div>
            <div style={{ paddingTop: 24 }}>
              {mainContent}
            </div>
          </div>
        ) : (
          <div style={{ paddingTop: 16, paddingBottom: 16 }}>
            {mainContent}
          </div>
        )}
      </div>
    </>
  );
}
